import {
  type IRaasPortalDefinition,
  type IRaasProjectViewState,
  type ProjectFormFieldKey,
  type ProjectTableColumnKey,
  type ProjectToolbarControlKey,
  RaasPortalId,
  RaasProjectPreviewSurfaceTrigger,
  RaasProjectViewActionKey,
  RaasProjectViewMode,
  RaasProjectWorkspaceSurface,
} from "./types";

export const RAAS_CLIENT_PORTAL_BASE_PATH = "/my-apps/raas-client-portal";
export const RAAS_INTERNAL_PORTAL_BASE_PATH = "/my-apps/raas-internal-portal";

export const RAAS_PORTAL_DEFINITIONS = {
  client: {
    id: RaasPortalId.Client,
    basePath: RAAS_CLIENT_PORTAL_BASE_PATH,
    defaultProjectViewMode: RaasProjectViewMode.PreviewAsClient,
    projectPreviewSurfaceTrigger: RaasProjectPreviewSurfaceTrigger.Never,
    projectViewActionKeys: [],
    projectViewModes: [RaasProjectViewMode.PreviewAsClient],
    tableColumnKeys: [
      "project",
      "hiring_for",
      "milestone",
      "status",
      "pipeline",
      "updated_at",
      "actions",
    ],
    toolbarControlKeys: [
      "search",
      "status_filter",
      "impersonation_selector",
      "create_project",
    ],
    projectFormFieldKeys: ["project_name", "job_title", "hiring_for"],
  },
  internal: {
    id: RaasPortalId.Internal,
    basePath: RAAS_INTERNAL_PORTAL_BASE_PATH,
    defaultProjectViewMode: RaasProjectViewMode.Edit,
    projectPreviewSurfaceTrigger: RaasProjectPreviewSurfaceTrigger.ManualPreview,
    projectViewActionKeys: [
      RaasProjectViewActionKey.EnterPreviewAsClient,
      RaasProjectViewActionKey.ExitPreviewAsClient,
      RaasProjectViewActionKey.SubmitToClient,
    ],
    projectViewModes: [
      RaasProjectViewMode.Edit,
      RaasProjectViewMode.PreviewAsClient,
    ],
    tableColumnKeys: [
      "project",
      "client",
      "acting_user_id",
      "hiring_for",
      "milestone",
      "status",
      "pipeline",
      "updated_at",
      "actions",
    ],
    toolbarControlKeys: [
      "search",
      "status_filter",
      "org_selector",
      "impersonation_selector",
      "client_filter",
      "assigned_to_filter",
      "create_project",
      "upload_template",
    ],
    projectFormFieldKeys: [
      "project_name",
      "job_title",
      "acting_user_id",
      "client",
      "hiring_for",
    ],
  },
} as const satisfies Record<RaasPortalId, IRaasPortalDefinition>;

export const DEFAULT_RAAS_PORTAL_DEFINITION = RAAS_PORTAL_DEFINITIONS.client;

export const getRaasPortalDefinition = ({
  pathname,
}: {
  pathname: string;
}): IRaasPortalDefinition =>
  Object.values(RAAS_PORTAL_DEFINITIONS).find((portalDefinition) =>
    pathname.startsWith(portalDefinition.basePath),
  ) || DEFAULT_RAAS_PORTAL_DEFINITION;

export const hasRaasPortalTableColumn = ({
  columnKey,
  portalDefinition,
}: {
  columnKey: ProjectTableColumnKey;
  portalDefinition: IRaasPortalDefinition;
}): boolean => portalDefinition.tableColumnKeys.includes(columnKey);

export const hasRaasPortalToolbarControl = ({
  controlKey,
  portalDefinition,
}: {
  controlKey: ProjectToolbarControlKey;
  portalDefinition: IRaasPortalDefinition;
}): boolean => portalDefinition.toolbarControlKeys.includes(controlKey);

export const hasRaasPortalProjectFormField = ({
  fieldKey,
  portalDefinition,
}: {
  fieldKey: ProjectFormFieldKey;
  portalDefinition: IRaasPortalDefinition;
}): boolean => portalDefinition.projectFormFieldKeys.includes(fieldKey);

export const canUseRaasPortalProjectViewMode = ({
  mode,
  portalDefinition,
}: {
  mode: RaasProjectViewMode;
  portalDefinition: IRaasPortalDefinition;
}): boolean => portalDefinition.projectViewModes.includes(mode);

export const getRaasProjectViewMode = ({
  portalDefinition,
  requestedMode,
}: {
  portalDefinition: IRaasPortalDefinition;
  requestedMode?: RaasProjectViewMode;
}): RaasProjectViewMode =>
  requestedMode &&
  canUseRaasPortalProjectViewMode({ mode: requestedMode, portalDefinition })
    ? requestedMode
    : portalDefinition.defaultProjectViewMode;

const isRaasProjectViewActionAvailable = ({
  actionKey,
  mode,
  portalDefinition,
}: {
  actionKey: RaasProjectViewActionKey;
  mode: RaasProjectViewMode;
  portalDefinition: IRaasPortalDefinition;
}): boolean => {
  if (!portalDefinition.projectViewActionKeys.includes(actionKey)) {
    return false;
  }

  if (actionKey === RaasProjectViewActionKey.EnterPreviewAsClient) {
    return (
      mode === RaasProjectViewMode.Edit &&
      canUseRaasPortalProjectViewMode({
        mode: RaasProjectViewMode.PreviewAsClient,
        portalDefinition,
      })
    );
  }

  if (actionKey === RaasProjectViewActionKey.SubmitToClient) {
    return mode === RaasProjectViewMode.Edit;
  }

  return (
    mode === RaasProjectViewMode.PreviewAsClient &&
    canUseRaasPortalProjectViewMode({
      mode: RaasProjectViewMode.Edit,
      portalDefinition,
    })
  );
};

export const getRaasProjectViewState = ({
  portalDefinition,
  requestedMode,
}: {
  portalDefinition: IRaasPortalDefinition;
  requestedMode?: RaasProjectViewMode;
}): IRaasProjectViewState => {
  const mode = getRaasProjectViewMode({ portalDefinition, requestedMode });
  const isManualPreview =
    mode === RaasProjectViewMode.PreviewAsClient &&
    requestedMode === RaasProjectViewMode.PreviewAsClient;

  return {
    actionKeys: portalDefinition.projectViewActionKeys.filter((actionKey) =>
      isRaasProjectViewActionAvailable({
        actionKey,
        mode,
        portalDefinition,
      }),
    ),
    mode,
    surface:
      portalDefinition.projectPreviewSurfaceTrigger ===
        RaasProjectPreviewSurfaceTrigger.ManualPreview &&
      isManualPreview
        ? RaasProjectWorkspaceSurface.Preview
        : RaasProjectWorkspaceSurface.Standard,
  };
};
